import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
    Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, Alert, Box, Chip, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Avatar,
    Card, CardContent, InputAdornment, MenuItem, Select, FormControl,
    ToggleButton, ToggleButtonGroup, Tabs, Tab, Divider, Pagination, Stack
} from '@mui/material';
import {
    Visibility, Refresh, Phone, Email, Business, Edit, Delete, Add,
    Search, CheckCircle, TrendingUp, Verified, History,
    Lock, LockOpen, DirectionsCar, EventAvailable, WorkspacePremium
} from '@mui/icons-material';
import { customModal } from './CustomModal';

const OwnerList = () => {
    const { t } = useTranslation();
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters & Sorting
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [membershipFilter, setMembershipFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date_desc');
    const [viewMode, setViewMode] = useState('grid');
    const [page, setPage] = useState(1);
    const rowsPerPage = 8;

    // Modals
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [profileTab, setProfileTab] = useState(0);
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', phoneNumber: '', businessName: '', status: 'active'
    });

    const fetchOwners = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/admin/owners');
            setOwners(response.data);
        } catch (err) {
            setError(err.response?.data?.message || t('error') || 'Failed to fetch owners');
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { fetchOwners(); }, [fetchOwners]);

    const handleView = async (owner) => {
        try {
            const detailRes = await axios.get(`/admin/owner/${owner._id}`);
            setSelectedOwner(detailRes.data);
        } catch (e) {
            setSelectedOwner(owner);
        }
        setProfileTab(0);
        setOpenProfileModal(true);
    };

    const handleAddClick = () => {
        setIsEditing(false);
        setFormData({ name: '', email: '', phoneNumber: '', businessName: '', status: 'active' });
        setOpenEditModal(true);
    };

    const handleEditClick = (owner) => {
        setIsEditing(true);
        setSelectedOwner(owner);
        setFormData({
            name: owner.name || '',
            email: owner.email || '',
            phoneNumber: owner.phoneNumber || '',
            businessName: owner.businessName || '',
            status: owner.status || 'active'
        });
        setOpenEditModal(true);
    };

    const handleDelete = async (id) => {
        const confirmed = await customModal.confirm(
            'Delete Owner',
            'Are you sure you want to delete this owner? This action cannot be undone.',
            { type: 'error', confirmText: 'Delete' }
        );
        if (confirmed) {
            try {
                await axios.delete(`/admin/owners/${id}`);
                fetchOwners();
            } catch (err) {
                setError('Failed to delete owner');
            }
        }
    };

    const handleToggleStatus = async (owner) => {
        const newStatus = owner.status === 'suspended' ? 'active' : 'suspended';
        const label = newStatus === 'suspended' ? 'Suspend' : 'Activate';
        const confirmed = await customModal.confirm(
            `${label} Owner`,
            `Are you sure you want to ${label.toLowerCase()} ${owner.name}?`,
            { type: newStatus === 'suspended' ? 'warning' : 'info', confirmText: label }
        );
        if (confirmed) {
            try {
                await axios.put(`/admin/owners/${owner._id}`, { status: newStatus });
                fetchOwners();
            } catch (err) {
                setError(`Failed to ${label.toLowerCase()} owner`);
            }
        }
    };

    const handleSave = async () => {
        try {
            if (isEditing) {
                await axios.put(`/admin/owners/${selectedOwner._id}`, formData);
            } else {
                await axios.post('/admin/owners', formData);
            }
            setOpenEditModal(false);
            fetchOwners();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save owner');
        }
    };

    // Summary stats
    const totalOwners = owners.length;
    const activeOwners = owners.filter(o => o.status === 'active').length;
    const verifiedOwners = owners.filter(o => o.isVerified).length;
    const membershipOwners = owners.filter(o => o.membershipPlanName && o.membershipStatus === 'active').length;
    const pendingOwners = owners.filter(o => o.status === 'pending').length;

    // Filtering
    const filteredOwners = owners.filter(owner => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = !term ||
            (owner.name || '').toLowerCase().includes(term) ||
            (owner._id || '').toLowerCase().includes(term) ||
            (owner.phoneNumber || '').toLowerCase().includes(term) ||
            (owner.email || '').toLowerCase().includes(term) ||
            (owner.businessName || '').toLowerCase().includes(term);

        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && owner.status === 'active') ||
            (statusFilter === 'pending' && owner.status === 'pending') ||
            (statusFilter === 'inactive' && owner.status === 'inactive');

        const matchesMembership = membershipFilter === 'all' ||
            (membershipFilter === 'active' && owner.membershipStatus === 'active') ||
            (membershipFilter === 'none' && (!owner.membershipPlanName || owner.membershipStatus === 'none')) ||
            (membershipFilter === 'expired' && owner.membershipStatus === 'expired');

        return matchesSearch && matchesStatus && matchesMembership;
    });

    const sortedOwners = [...filteredOwners].sort((a, b) => {
        if (sortBy === 'date_desc') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        return 0;
    });

    const pageCount = Math.ceil(sortedOwners.length / rowsPerPage);
    const paginatedOwners = sortedOwners.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const getMembershipColor = (status) => {
        if (status === 'active') return 'success';
        if (status === 'pending') return 'warning';
        if (status === 'expired') return 'error';
        return 'default';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, backgroundColor: '#f8fafc', minHeight: '90vh' }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Owner Management Hub
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage fleet owners, memberships, and vehicle registers
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddClick}
                        disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                    >
                        Add Owner
                    </Button>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchOwners} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* KPI Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {[
                    { label: 'TOTAL OWNERS', value: totalOwners, color: '#2563eb', icon: <Business fontSize="small" />, sub: 'Registered' },
                    { label: 'ACTIVE OWNERS', value: activeOwners, color: '#16a34a', icon: <CheckCircle fontSize="small" />, sub: 'Connected' },
                    { label: 'VERIFIED', value: verifiedOwners, color: '#9333ea', icon: <Verified fontSize="small" />, sub: 'Docs Approved' },
                    { label: 'MEMBERSHIPS', value: membershipOwners, color: '#ea580c', icon: <WorkspacePremium fontSize="small" />, sub: 'Active Plans' },
                    { label: 'PENDING', value: pendingOwners, color: '#0284c7', icon: <EventAvailable fontSize="small" />, sub: 'Awaiting Approval' },
                ].map(card => (
                    <Grid item xs={12} sm={6} md={2.4} key={card.label}>
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: `4px solid ${card.color}` }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{card.label}</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: card.color, my: 0.5 }}>{card.value}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {card.icon} {card.sub}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Control Bar */}
            <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 4, bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth size="small"
                            placeholder="Search by name, ID, phone, email, business..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#64748b' }} /></InputAdornment>
                            }}
                            sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}>
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <Select value={membershipFilter} onChange={(e) => { setMembershipFilter(e.target.value); setPage(1); }}
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}>
                                <MenuItem value="all">All Plans</MenuItem>
                                <MenuItem value="active">Active Plan</MenuItem>
                                <MenuItem value="none">No Plan</MenuItem>
                                <MenuItem value="expired">Expired Plan</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                                sx={{ bgcolor: '#f1f5f9', borderRadius: 2, '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600 }}>
                                <MenuItem value="date_desc">Newest Joined</MenuItem>
                                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6} sm={4} md={2} sx={{ textAlign: 'right' }}>
                        <ToggleButtonGroup value={viewMode} exclusive onChange={(e, m) => m && setViewMode(m)} size="small" sx={{ bgcolor: '#f1f5f9', borderRadius: 2 }}>
                            <ToggleButton value="grid" sx={{ px: 1.5 }}><TrendingUp fontSize="small" /></ToggleButton>
                            <ToggleButton value="table" sx={{ px: 1.5 }}><History fontSize="small" /></ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
            </Paper>

            {/* Content */}
            {sortedOwners.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 3, py: 4 }}>No owners match the selected filters.</Alert>
            ) : viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {paginatedOwners.map((owner) => {
                        const isActive = owner.status === 'active';
                        const hasMembership = owner.membershipPlanName && owner.membershipStatus === 'active';

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={owner._id}>
                                <Card elevation={0} sx={{
                                    borderRadius: 4, border: '1px solid #e2e8f0',
                                    transition: 'all 0.25s ease',
                                    '&:hover': { boxShadow: '0 12px 24px -10px rgba(0,0,0,0.12)', transform: 'translateY(-3px)', borderColor: '#cbd5e1' },
                                    overflow: 'hidden'
                                }}>
                                    <Box sx={{ height: 6, bgcolor: isActive ? '#16a34a' : '#94a3b8' }} />
                                    <CardContent sx={{ p: 2.5 }}>
                                        {/* Header */}
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ width: 56, height: 56, bgcolor: '#9333ea', fontWeight: 700, fontSize: 22 }}>
                                                {owner.name ? owner.name.charAt(0).toUpperCase() : 'O'}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: '#0f172a', fontSize: 16 }}>
                                                        {owner.name || 'Unnamed Owner'}
                                                    </Typography>
                                                    {owner.isVerified && (
                                                        <Tooltip title="Verified"><Verified sx={{ fontSize: 16, color: '#9333ea' }} /></Tooltip>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                                    {owner.businessName || 'Independent Owner'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                    <Chip label={isActive ? 'Active' : (owner.status || 'Pending')} size="small"
                                                        color={isActive ? 'success' : owner.status === 'pending' ? 'warning' : 'default'}
                                                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                                                    {hasMembership && (
                                                        <Chip label="Member" size="small"
                                                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#fef08a', color: '#854d0e' }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Contact Info */}
                                        <Stack spacing={1} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="body2" color="text.secondary">{owner.phoneNumber || '—'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="body2" color="text.secondary" noWrap>{owner.email || '—'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DirectionsCar sx={{ fontSize: 16, color: '#64748b' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                                    {owner.vehicles ? `${owner.vehicles.length} vehicle(s)` : 'No vehicles'}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {/* Membership Badge */}
                                        <Box sx={{ p: 1.5, bgcolor: hasMembership ? '#f0fdf4' : '#f8fafc', borderRadius: 2, mb: 2, border: `1px solid ${hasMembership ? '#dcfce7' : '#e2e8f0'}` }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>MEMBERSHIP</Typography>
                                            {hasMembership ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <WorkspacePremium sx={{ fontSize: 16, color: '#16a34a' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#15803d' }}>{owner.membershipPlanName}</Typography>
                                                    <Chip label={owner.membershipStatus} size="small" color={getMembershipColor(owner.membershipStatus)} sx={{ height: 18, fontSize: 10 }} />
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No active plan</Typography>
                                            )}
                                            {owner.membershipExpiresAt && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Expires: {new Date(owner.membershipExpiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Actions */}
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Button size="small" variant="contained" disableElevation onClick={() => handleView(owner)}
                                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>
                                                View Profile
                                            </Button>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="Edit Owner">
                                                    <IconButton size="small" onClick={() => handleEditClick(owner)} sx={{ bgcolor: '#f1f5f9' }}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={owner.status === 'suspended' ? 'Activate' : 'Suspend'}>
                                                    <IconButton size="small" onClick={() => handleToggleStatus(owner)}
                                                        sx={{ bgcolor: owner.status === 'suspended' ? '#fef2f2' : '#f1f5f9', color: owner.status === 'suspended' ? '#ef4444' : '#64748b' }}>
                                                        {owner.status === 'suspended' ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => handleDelete(owner._id)} sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                /* Table View */
                <TableContainer component={Paper} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Owner</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Contact</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Membership</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Vehicles</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }}>Joined</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569' }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedOwners.map((owner) => (
                                <TableRow key={owner._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ width: 40, height: 40, bgcolor: '#9333ea', fontWeight: 700 }}>
                                                {owner.name ? owner.name.charAt(0) : 'O'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a' }}>{owner.name || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary">{owner.businessName || 'Independent'}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{owner.phoneNumber || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{owner.email || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={owner.status || 'pending'} size="small"
                                            color={owner.status === 'active' ? 'success' : owner.status === 'pending' ? 'warning' : 'default'}
                                            sx={{ fontWeight: 700 }} />
                                    </TableCell>
                                    <TableCell>
                                        {owner.membershipPlanName ? (
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{owner.membershipPlanName}</Typography>
                                                <Chip label={owner.membershipStatus} size="small" color={getMembershipColor(owner.membershipStatus)} />
                                            </Box>
                                        ) : <Chip label="No Plan" size="small" variant="outlined" />}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{owner.vehicles ? owner.vehicles.length : '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {owner.createdAt ? new Date(owner.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="secondary" onClick={() => handleView(owner)}><Visibility fontSize="small" /></IconButton>
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(owner)}><Edit fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(owner._id)}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination count={pageCount} page={page} onChange={(e, val) => setPage(val)} color="primary" shape="rounded" />
                </Box>
            )}

            {/* ── COMPREHENSIVE OWNER PROFILE MODAL ── */}
            <Dialog open={openProfileModal} onClose={() => setOpenProfileModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                {selectedOwner && (
                    <>
                        <DialogTitle sx={{ p: 3, pb: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: '#9333ea', fontWeight: 800, fontSize: 24 }}>
                                    {selectedOwner.name ? selectedOwner.name.charAt(0).toUpperCase() : 'O'}
                                </Avatar>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{selectedOwner.name}</Typography>
                                        {selectedOwner.isVerified && <Chip label="Verified" size="small" sx={{ bgcolor: '#9333ea', color: 'white', fontWeight: 700 }} />}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedOwner.businessName || 'Independent Owner'} · Joined: {selectedOwner.createdAt ? new Date(selectedOwner.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Tabs value={profileTab} onChange={(e, val) => setProfileTab(val)} sx={{ mt: 2 }}>
                                <Tab label="Contact & Business" />
                                <Tab label="Membership" />
                                <Tab label="Vehicles" />
                            </Tabs>
                        </DialogTitle>

                        <DialogContent sx={{ p: 3 }}>
                            {profileTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#0f172a' }}>Contact Details</Typography>
                                            <Stack spacing={1.5}>
                                                <Typography variant="body2"><b>Full Name:</b> {selectedOwner.name || '—'}</Typography>
                                                <Typography variant="body2"><b>Email:</b> {selectedOwner.email || '—'}</Typography>
                                                <Typography variant="body2"><b>Phone:</b> {selectedOwner.phoneNumber || '—'}</Typography>
                                                <Typography variant="body2"><b>Account Status:</b>
                                                    <Chip label={selectedOwner.status || 'pending'} size="small" sx={{ ml: 1, fontWeight: 700 }}
                                                        color={selectedOwner.status === 'active' ? 'success' : 'default'} />
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ p: 2.5, bgcolor: '#f5f3ff', borderRadius: 3, border: '1px solid #ede9fe' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#6d28d9' }}>Business Details</Typography>
                                            <Stack spacing={1.5}>
                                                <Typography variant="body2"><b>Business Name:</b> {selectedOwner.businessName || '—'}</Typography>
                                                <Typography variant="body2"><b>Owner ID:</b> {selectedOwner._id}</Typography>
                                                <Typography variant="body2"><b>Verified:</b> {selectedOwner.isVerified ? '✅ Yes' : '❌ No'}</Typography>
                                            </Stack>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}

                            {profileTab === 1 && (
                                <Box>
                                    {selectedOwner.membershipPlanName ? (
                                        <Box sx={{ p: 3, bgcolor: '#f0fdf4', borderRadius: 3, border: '1px solid #dcfce7' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <WorkspacePremium sx={{ fontSize: 40, color: '#16a34a' }} />
                                                <Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#15803d' }}>{selectedOwner.membershipPlanName}</Typography>
                                                    <Chip label={selectedOwner.membershipStatus} size="small" color={getMembershipColor(selectedOwner.membershipStatus)} sx={{ fontWeight: 700 }} />
                                                </Box>
                                            </Box>
                                            {selectedOwner.membershipExpiresAt && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <b>Renewal Date:</b> {new Date(selectedOwner.membershipExpiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Alert severity="info" sx={{ borderRadius: 3 }}>
                                            This owner has not subscribed to any membership plan yet.
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            {profileTab === 2 && (
                                <Box>
                                    {selectedOwner.vehicles && selectedOwner.vehicles.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {selectedOwner.vehicles.map((v) => (
                                                <Grid item xs={12} sm={6} key={v._id}>
                                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                            <DirectionsCar color="primary" />
                                                            <Typography variant="body1" sx={{ fontWeight: 800 }}>{v.vehicleNumber}</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary">{v.type} · {v.model}</Typography>
                                                        {v.verificationStatus && (
                                                            <Chip label={v.verificationStatus} size="small" sx={{ mt: 1, fontWeight: 700 }}
                                                                color={v.verificationStatus === 'approved' ? 'success' : v.verificationStatus === 'rejected' ? 'error' : 'warning'} />
                                                        )}
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Alert severity="info">No vehicles registered under this owner.</Alert>
                                    )}
                                </Box>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <Button onClick={() => handleEditClick(selectedOwner)} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>Edit</Button>
                            <Button onClick={() => setOpenProfileModal(false)} variant="contained" sx={{ borderRadius: 2, px: 4, bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* ── ADD / EDIT MODAL ── */}
            <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{isEditing ? 'Edit Owner Profile' : 'Add New Owner'}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Full Name" fullWidth value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                        <TextField label="Email Address" fullWidth value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                        <TextField label="Phone Number" fullWidth value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} />
                        <TextField label="Business Name" fullWidth value={formData.businessName} onChange={(e) => setFormData({ ...formData, businessName: e.target.value })} />
                        <TextField label="Account Status" select fullWidth SelectProps={{ native: true }} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="inactive">Inactive</option>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setOpenEditModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 3, bgcolor: '#9333ea', '&:hover': { bgcolor: '#7e22ce' } }}>
                        {isEditing ? 'Save Changes' : 'Add Owner'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OwnerList;
